/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 *
 * Data hooks for Cold Storage. The company id is the query key, so a company change is a
 * clean fetch; the state polls every minute like the map pages, except while a layout is
 * being edited, when a refetch could snap the markers back under the user's cursor.
 */

import { useQuery } from '@tanstack/react-query'

import { useApi } from '@/hooks'

import { fetchImageSrc, fetchImages, fetchInStoreState } from './api'
import { MODES, type ModeConfig } from './mode'

export const IMAGES_QUERY_KEY = 'cold-storage-images'

export const useInStoreState = (
  companyId: number | undefined,
  { paused = false, mode = MODES.coldStorage }: { paused?: boolean; mode?: ModeConfig } = {}
) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [mode.stateQueryKey, companyId],
    queryFn: () => fetchInStoreState(api, mode.statePath, companyId!),
    enabled: !!companyId,
    staleTime: 30_000,
    refetchInterval: paused ? false : 60_000,
  })
}

export const useCompanyImages = (companyId?: number) => {
  const { api } = useApi()
  return useQuery({
    queryKey: [IMAGES_QUERY_KEY, companyId],
    queryFn: () => fetchImages(api, companyId!),
    enabled: !!companyId,
    staleTime: 5 * 60_000,
  })
}

/**
 * The signed URL for an image. The token lives five minutes; caching the URL for four
 * means flipping between locations that share a floor plan does not sign it again.
 */
export const useImageSrc = (imageId?: number) => {
  const { api } = useApi()
  return useQuery({
    queryKey: ['cold-storage-image-src', imageId],
    queryFn: () => fetchImageSrc(api, imageId!),
    enabled: !!imageId,
    staleTime: 4 * 60_000,
    gcTime: 4 * 60_000,
  })
}
